import React, {
  createContext,
  ReactNode,
  useContext,
  useRef,
  useState,
} from "react";
import FollowingListProvider from "./FollowingListProvider";
import { FollowingBranchesList } from "../../../model/notices/FollowingBranchListMode";
import Loader from "../../../widget/common/ui/Loader";
import { AlertConfirm, AlertMesage } from "../../../widget/common/ui/Alerts";
import UtilityHelper from "../../../service/utilities/UtilityHelper";

type FollowingListContextType = {
  followingBranchList: Array<FollowingBranchesList> | null;
  selectedBranchIds: Array<string>;
  deleteStatus: boolean;
  getFollowingListAPI: () => void;
  onClickBranch: (branch: FollowingBranchesList) => void;
  toggleClickDelete: () => void;
  submitDeleteConfirm: () => void;

  showSearch: boolean;
  searchValue: string;
  handleOnChange: (name: string, value: string) => void;
  clearSearch: () => void;
  updateShowSearch: () => void;
};

const FollowingListContext = createContext<
  FollowingListContextType | undefined
>(undefined);

export const FollowingListContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const searchValueRef = useRef<string>("");
  const followingBranchListRef = useRef<Array<FollowingBranchesList> | []>([]);

  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");

  const [selectedBranchIds, setSelectedBranchIds] = useState<Array<string>>([]);
  const [deleteStatus, setDeleteStatus] = useState<boolean>(false);
  const [followingBranchList, setFollowingBranchList] =
    useState<Array<FollowingBranchesList> | null>(null);

  const resetValues = () => {
    setFollowingBranchList(followingBranchListRef.current);
    setSearchValue("");
    searchValueRef.current = "";
    setShowSearch(false);
  };

  const onClickBranch = (branch: FollowingBranchesList) => {
    if (selectedBranchIds.includes(branch?.branch_id!)) {
      setSelectedBranchIds(
        selectedBranchIds.filter((item) => item !== branch?.branch_id!)
      );
    } else {
      setSelectedBranchIds([...selectedBranchIds, branch?.branch_id!]);
    }
  };

  const getFollowingListAPI = async () => {
    const FollowingBranchListModel =
      await FollowingListProvider.getFollowingBranchListAPI();
    if (FollowingBranchListModel !== null) {
      const followingBranchesList: Array<FollowingBranchesList> | null =
        FollowingBranchListModel?.result?.following_branches_list!;

      setFollowingBranchList(followingBranchesList);
      followingBranchListRef.current = followingBranchesList;
    }
  };

  const submitDeleteConfirm = async () => {
    if (selectedBranchIds.length > 0) {
      AlertConfirm({
        title: "would_you_like_to_unfollow_these_branches",
        onConfirm: () => {
          submitDeleteAPI();
        },
      });
    }
  };

  const submitDeleteAPI = async () => {
    Loader.modal();
    const statusResult: boolean =
      await FollowingListProvider.deleteFollowingAPI(selectedBranchIds);
    Loader.modal(false);

    if (statusResult) {
      AlertMesage({
        title: "you_ve_successfully_unfollowed_these_branches",
        onClick: () => {
          FollowingListProvider.navigate(-1);
        },
      });
    }
  };

  const toggleClickDelete = () => {
    setDeleteStatus(!deleteStatus);
    setSelectedBranchIds([]);

    resetValues();
  };

  const handleOnChange = (fieldName: string, fieldValue: string) => {
    fieldName = fieldName.toLowerCase();
    if (fieldName === `search-following`) {
      setSearchValue(fieldValue);
      searchValueRef.current = fieldValue;

      const filtered: Array<FollowingBranchesList> | undefined =
        followingBranchListRef.current?.filter((branch) =>
          Object.values(branch)
            .join(" ")
            .toLowerCase()
            .includes(searchValueRef.current.toLowerCase())
        );

      setFollowingBranchList(filtered!);
    }
  };

  const clearSearch = () => {
    if (UtilityHelper.isEmpty(searchValueRef.current)) {
      updateShowSearch();
    }
    setFollowingBranchList(followingBranchListRef.current);
    setSearchValue("");
    searchValueRef.current = "";
  };

  const updateShowSearch = () => {
    setShowSearch(!showSearch);
  };

  return (
    <FollowingListContext.Provider
      value={{
        followingBranchList,
        deleteStatus,
        selectedBranchIds,
        getFollowingListAPI,
        onClickBranch,
        toggleClickDelete,
        submitDeleteConfirm,

        showSearch,
        searchValue,
        handleOnChange,
        clearSearch,
        updateShowSearch,
      }}
    >
      {children}
    </FollowingListContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const FollowingListContextImport = () => {
  const context = useContext(FollowingListContext);
  if (!context) {
    throw new Error(
      "FollowingListContextImport must be used within a FollowingListContextProvider"
    );
  }
  return context;
};
